import { messages } from '@databases/intl/messages';
import { DatabaseFieldType } from '@databases/types/database';
import { type DatabaseFilter, DatabaseFilterType } from '@databases/types/database/view';
import { useMemo } from 'react';
import { useIntl, type IntlShape } from 'react-intl-next';

type FilterTypeLabelOptionsNot = {
  fieldType?: DatabaseFieldType;
  formatMessage: IntlShape['formatMessage'];
  not?: boolean;
};

type FilterTypeLabelOptionsOrEqual = {
  fieldType?: DatabaseFieldType;
  formatMessage: IntlShape['formatMessage'];
  orEqual?: boolean;
};

export const filterTypeLabelMap = {
  [DatabaseFilterType.EQUALS]: (options: FilterTypeLabelOptionsNot) => {
    const { fieldType, formatMessage, not } = options;

    if (not) {
      return fieldType === DatabaseFieldType.DATE
        ? formatMessage(messages['toolbar-filter.type-picker-date-is-not-on-text'])
        : fieldType === DatabaseFieldType.NUMBER
        ? '≠'
        : formatMessage(messages['toolbar-filter.type-picker-is-not-text']);
    }

    return fieldType === DatabaseFieldType.DATE
      ? formatMessage(messages['toolbar-filter.type-picker-date-is-on-text'])
      : fieldType === DatabaseFieldType.NUMBER
      ? '='
      : formatMessage(messages['toolbar-filter.type-picker-is-text']);
  },
  [DatabaseFilterType.CONTAINS]: ({ formatMessage, not }: FilterTypeLabelOptionsNot) => {
    return not
      ? formatMessage(messages['toolbar-filter.type-picker-does-not-contain-text'])
      : formatMessage(messages['toolbar-filter.type-picker-contains-text']);
  },
  [DatabaseFilterType.EMPTY]: ({ formatMessage, not }: FilterTypeLabelOptionsNot) => {
    return not
      ? formatMessage(messages['toolbar-filter.type-picker-is-not-empty-text'])
      : formatMessage(messages['toolbar-filter.type-picker-is-empty-text']);
  },
  [DatabaseFilterType.GREATER]: (options: FilterTypeLabelOptionsOrEqual) => {
    const { fieldType, formatMessage, orEqual } = options;

    if (orEqual) {
      return fieldType === DatabaseFieldType.DATE
        ? formatMessage(messages['toolbar-filter.type-picker-is-on-or-after-text'])
        : fieldType === DatabaseFieldType.NUMBER
        ? '≥'
        : formatMessage(
            messages['toolbar-filter.type-picker-is-greater-than-or-equal-text']
          );
    }

    return fieldType === DatabaseFieldType.DATE
      ? formatMessage(messages['toolbar-filter.type-picker-is-after-text'])
      : fieldType === DatabaseFieldType.NUMBER
      ? '>'
      : formatMessage(messages['toolbar-filter.type-picker-is-greater-than-text']);
  },
  [DatabaseFilterType.LESS]: (options: FilterTypeLabelOptionsOrEqual) => {
    const { fieldType, formatMessage, orEqual } = options;

    if (orEqual) {
      return fieldType === DatabaseFieldType.DATE
        ? formatMessage(messages['toolbar-filter.type-picker-is-on-or-before-text'])
        : fieldType === DatabaseFieldType.NUMBER
        ? '≤'
        : formatMessage(
            messages['toolbar-filter.type-picker-is-less-than-or-equal-text']
          );
    }

    return fieldType === DatabaseFieldType.DATE
      ? formatMessage(messages['toolbar-filter.type-picker-is-before-text'])
      : fieldType === DatabaseFieldType.NUMBER
      ? '<'
      : formatMessage(messages['toolbar-filter.type-picker-is-less-than-text']);
  },
} as const;

type UseFilterTypeLabelOptions = {
  fieldType?: DatabaseFieldType;
  filter: DatabaseFilter;
};

export const useFilterTypeLabel = ({ fieldType, filter }: UseFilterTypeLabelOptions) => {
  const { formatMessage } = useIntl();

  return useMemo(() => {
    if (filter.type === DatabaseFilterType.EQUALS) {
      return filterTypeLabelMap[DatabaseFilterType.EQUALS]({
        fieldType,
        formatMessage,
        not: filter.not,
      });
    }

    if (filter.type === DatabaseFilterType.CONTAINS) {
      return filterTypeLabelMap[DatabaseFilterType.CONTAINS]({
        fieldType,
        formatMessage,
        not: filter.not,
      });
    }

    if (filter.type === DatabaseFilterType.EMPTY) {
      return filterTypeLabelMap[DatabaseFilterType.EMPTY]({
        fieldType,
        formatMessage,
        not: filter.not,
      });
    }

    if (filter.type === DatabaseFilterType.GREATER) {
      return filterTypeLabelMap[DatabaseFilterType.GREATER]({
        fieldType,
        formatMessage,
        orEqual: filter.orEqual,
      });
    }

    if (filter.type === DatabaseFilterType.LESS) {
      return filterTypeLabelMap[DatabaseFilterType.LESS]({
        fieldType,
        formatMessage,
        orEqual: filter.orEqual,
      });
    }

    return undefined;
  }, [fieldType, filter, formatMessage]);
};
